import { graphql } from 'gatsby';
import React from 'react';
import { useMicrosite } from '../../contexts/franchise/microsite-context.tsx';
import AmexIcon from '../../images/credit-cards/amex.svg';
import DiscoverIcon from '../../images/credit-cards/discover.svg';
import MasterCardIcon from '../../images/credit-cards/mastercard.svg';
import VisaIcon from '../../images/credit-cards/visa.svg';
import Container from '../atoms/Container';
import FranchiseLicenses from '../molecules/franchise-licenses';
import { colors } from '../primitives/colors/index.tsx';
import paddingDefault from '../utils/paddingDefault';
import ServiceArea from './ServiceArea';
import ServicesDetail from './ServicesDetail';

const FranchiseAboutInfo = ({ data }) => {
    const {
        headline,
        quick_links: quickLinks,
    } = data;

    const microsite = useMicrosite();

    const copy = (microsite?.franchise?.profileExcerpt || '');

    const {
        affiliations,
        awards,
        certifications,
    } = microsite?.franchise?.aboutUs || {};

    const licenses = microsite?.franchise?.licenses || {};
    const paymentMethods = microsite?.franchise?.paymentMethods || {};

    return (
        <>
            <ServicesDetail
                data={{
                    headline: headline || 'About Us',
                    copy,
                    quicklinks: {
                        heading: 'Quick Links',
                        links: quickLinks,
                    },
                }}
            />
            <ServiceArea
                data={{
                    title: 'Service Area',
                }}
                pageData={data}
            />
            <Container css={paddingDefault(null, true)}>
                <div css={styles.containerInnerWrap}>
                    {(certifications && certifications.length > 0) && (
                        <div css={styles.infoItemWrap}>
                            <h4 css={styles.heading}>Certifications</h4>
                            <ul css={styles.unorderedList}>
                                {certifications.map(({ description }) => (
                                    <li>{description}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {licenses && Object.values(licenses).filter(x => !!x).length > 0 && (
                        <div css={styles.infoItemWrap}>
                            <h4 css={styles.heading}>Licenses</h4>
                            <FranchiseLicenses />
                        </div>
                    )}
                    {paymentMethods && Object.values(paymentMethods).filter(x => !!x).length > 0 && (
                        <div css={styles.infoItemWrap}>
                            <h4 css={styles.heading}>Payment Info</h4>
                            <strong>The following cards are accepted:</strong>
                            <div css={styles.creditCardIconWrap}>
                                {paymentMethods?.visa && <VisaIcon css={styles.creditCardIcon} />}
                                {paymentMethods?.masterCard && <MasterCardIcon css={styles.creditCardIcon} />}
                                {paymentMethods?.americanExpress && <AmexIcon css={styles.creditCardIcon} />}
                                {paymentMethods?.discover && <DiscoverIcon css={styles.creditCardIcon} />}
                            </div>
                        </div>
                    )}
                    {(awards && awards.length > 0) && (
                        <div css={styles.infoItemWrap}>
                            <h4 css={styles.heading}>Awards</h4>
                            <ul css={styles.unorderedList}>
                                {awards.map(({ description }) => (
                                    <li>{description}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {(affiliations && affiliations.length > 0) && (
                        <div css={styles.infoItemWrap}>
                            <h4 css={styles.heading}>Affiliations</h4>
                            <ul css={styles.unorderedList}>
                                {affiliations.map(({ description }) => (
                                    <li>{description}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Container>
        </>
    );
};

const styles = {
    containerInnerWrap: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        gap: '2rem',
        width: '100%',
        '@media (min-width: 1024px)': {
            flexDirection: 'row',
        },
    },
    infoItemWrap: {
        width: '100%',
        '@media (min-width: 1024px)': {
            width: 'calc(50% - 20px)',
        },
    },
    heading: {
        color: colors.primaryOrange,
        paddingBottom: '0.5rem',
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        fontWeight: 600,
    },
    unorderedList: {
        paddingLeft: '1.25rem',
        listStyleType: 'disc',
    },
    creditCardIcon: {
        width: '4rem',
        height: '100%',
    },
    creditCardIconWrap: {
        display: 'flex',
        paddingTop: '1rem',
        columnGap: '1rem',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
};

export const query = graphql`
  fragment FranchiseAboutInfoData on CmsPageBuilderBlocks {
    franchise_about_info {
      headline
      quick_links {
        title
        href
      }
    }
  }
`;

export default FranchiseAboutInfo;
